import { MapGrouping } from './map-grouping';
import { Layers, MapLayer } from './map-layer';
import { ZoneObject } from '@ng-cloud/badger-core/map/objects/zone-object';
import { NavZone } from '@ng-cloud/badger-core/models/nav-zone';
import * as _ from 'lodash';
import chroma from 'chroma-js';

@Layers([{ name: 'navZones', zIndex: 6 }])
export class NavZoneGrouping extends MapGrouping<NavZone, ZoneObject> {
  private readonly DEFAULT_FONT_SIZE = 14;

  readonly modeColors = {
    [NavZone.KEEP_OUT_MODE]: '#be3232',
    [NavZone.KEEP_IN_MODE]: '#307930',
    [NavZone.SPEED_MODE]: '#eda54c'
  };

  private readonly SPEED_BASELINE = 0.3;
  private readonly SPEED_MIN = 0.1;
  private readonly SPEED_MAX = 1.5;

  private readonly SPEED_BASELINE_FALLBACK = 0.3;

  // Main “punch” knobs
  private readonly SPEED_CONTRAST_EXPONENT = 0.55; // smaller => punchier near baseline
  private readonly SPEED_BUCKETS = 0;              // 0 disables; try 10–14 to make 0.1 steps pop

  // Neutral + endpoints (easy to tune)
  private readonly SPEED_NEUTRAL = 'rgba(136,135,128,0.54)';

  // Below baseline: neutral -> amber -> red
  private readonly SPEED_SLOW_MID = '#fbbf24';
  private readonly SPEED_SLOW_MAX = '#ef4444';

  // Above baseline: neutral -> cyan -> purple (more differentiable than green-only)
  private readonly SPEED_FAST_MID = '#a3e635';
  private readonly SPEED_FAST_MAX = '#14532d';

  // Opacity + outline
  private readonly SPEED_ZONE_FILL_OPACITY = 0.45; // try 0.45–0.60 for more pop
  private readonly SPEED_ZONE_STROKE = '#111';
  private readonly SPEED_ZONE_STROKE_WIDTH = 2;

  private clamp01(x: number) {
    return Math.max(0, Math.min(1, x));
  }

  private clamp(n: number, a: number, b: number) {
    return Math.max(a, Math.min(b, n));
  }

  private contrastCurve(t: number): number {
    return Math.pow(this.clamp01(t), this.SPEED_CONTRAST_EXPONENT);
  }

  private quantize(t: number): number {
    if (!this.SPEED_BUCKETS || this.SPEED_BUCKETS < 2) {
      return t;
    }
    const b = this.SPEED_BUCKETS;
    return Math.round(this.clamp01(t) * b) / b;
  }

  public refreshEntry(entry: any, navZone: NavZone): void {
    if (!entry?.object) {
      return;
    }

    const color = this.getColor(navZone);

    const poly = entry.object.getFabric?.();
    if (poly?.set) {
      poly.set({
        fill: color.polygonOptions?.fill,
        opacity: color.polygonOptions?.opacity,
        stroke: color.polygonOptions?.stroke,
        strokeWidth: color.polygonOptions?.strokeWidth,
        strokeUniform: color.polygonOptions?.strokeUniform ?? true
      });
      poly.setCoords?.();
    }

    const label =
      entry.object.getLabelFabric?.() ||
      entry.object.label?.getFabric?.() ||
      entry.object.label?.getFabric?.() ||
      entry.object.labelFabric ||
      entry.object.label;

    if (label?.set && color.labelOptions) {
      label.set({
        fontSize: color.labelOptions.fontSize,
        textBackgroundColor: color.labelOptions.textBackgroundColor
      });
    }

    const speed = navZone.speed_mps;
    const txt = (typeof speed === 'number' && Number.isFinite(speed)) ? `${speed.toFixed(1)} m/s` : '';
    if (label?.set) {
      label.set({ text: txt });
    }

    label?.setCoords?.();
  }


  public setSpeedBaseline(baseline: number) {
    this.options = this.options ?? {};
    this.options.speedBaseline = baseline;
  }

  private getSpeedBaseline(): number {
    const raw = this.options?.speedBaseline;
    const n = typeof raw === 'number' ? raw : Number(raw);
    const baseline = Number.isFinite(n) ? n : this.SPEED_BASELINE_FALLBACK;
    return this.clamp(baseline, this.SPEED_MIN, this.SPEED_MAX);
  }

  private speedToColor(speed?: number): string {
    const base = this.getSpeedBaseline();
    const min = this.SPEED_MIN;
    const max = this.SPEED_MAX;
    const s = this.clamp(speed ?? base, min, max);

    if (Math.abs(s - base) < 1e-9) {
      return this.SPEED_NEUTRAL;
    }

    // Perceptual scales
    const slowScale = chroma
      .scale([this.SPEED_NEUTRAL, this.SPEED_SLOW_MID, this.SPEED_SLOW_MAX])
      .mode('lch');

    const fastScale = chroma
      .scale([this.SPEED_NEUTRAL, this.SPEED_FAST_MID, this.SPEED_FAST_MAX])
      .mode('lch');

    if (s < base) {
      const tRaw = (base - s) / (base - min);          // 0..1
      let t = this.contrastCurve(tRaw);
      t = this.quantize(t);
      return slowScale(t).hex();
    }
    else {
      const tRaw = (s - base) / (max - base);          // 0..1
      let t = this.contrastCurve(tRaw);
      t = this.quantize(t);
      return fastScale(t).hex();
    }
  }

  private prevStaleCheck: number[] = [];

  constructor(public options: any = {}, public layerName: string = 'navZones', public zIndex: number = 1) {
    super(options);
    this.layers = [new MapLayer(Object.assign({}, { name: layerName, zIndex: zIndex }, { grouping: this }))];
  }

  createObject(data: NavZone): ZoneObject {
    const points = data.points.map(pt => this.storeMap.pointToPixels(pt));
    const opts = Object.assign(
      { speedBaseline: this.SPEED_BASELINE },
      this.getColor(data),
      this.options
    );
    return new ZoneObject(data, points, opts);
  }

  protected getFontSize(): number {
    return Math.max(Math.ceil(this.storeMap.width / 150), this.DEFAULT_FONT_SIZE);
  }

  isStale(navZoneIDs: number[]): boolean {
    if (!navZoneIDs) {
      return false;
    }
    const sortedIds = navZoneIDs.sort();
    const stale = !_.isEqual(sortedIds, this.prevStaleCheck);
    this.prevStaleCheck = sortedIds;
    return stale;
  }

  protected getColor(navZone: NavZone) {
    if (navZone.mode === NavZone.SPEED_MODE) {
      const fill = this.speedToColor(navZone.speed_mps);
      return {
        polygonOptions: {
          fill,
          opacity: this.SPEED_ZONE_FILL_OPACITY,
          stroke: this.SPEED_ZONE_STROKE,
          strokeWidth: this.SPEED_ZONE_STROKE_WIDTH,
          strokeUniform: true as any
        },
        labelOptions: { textBackgroundColor: this.modeColors[navZone.mode], fontSize: this.getFontSize() }
      };
    }

    return {
      polygonOptions: { fill: this.modeColors[navZone.mode] },
      labelOptions: { textBackgroundColor: this.modeColors[navZone.mode], fontSize: this.getFontSize() }
    };
  }
}
